import { apiClient } from '@/api/api'
import { hasEmailLinksApi } from '../flags'

/**
 * Calls behind the screens opened from email links (Figma `3603:68382`).
 *
 * Every call runs from the page, in the browser: mail scanners fetch the link
 * itself, never the API, so opening a link in a scanner changes nothing.
 *
 * Mocks (capability off, dev only) reach every state through the token:
 *   `expired`  → link expired, `used` → already done, anything else → success.
 */

const MOCK_LATENCY_MS = 500
export const EMAIL_LINKS_MOCK_EXPIRED_TOKEN = 'expired'
export const EMAIL_LINKS_MOCK_USED_TOKEN = 'used'
const MOCK_NEW_EMAIL = 'anna.new@example.com'

/** expired: too old, replaced or malformed. already: the goal is already reached. */
export type EmailLinkFailure = 'expired' | 'already' | 'unknown'

export class EmailLinkError extends Error {
  constructor(public readonly reason: EmailLinkFailure) {
    super(reason)
  }
}

/** An axios error, or the `QueryError` mutations rethrow (`statusCode` / `details`). */
interface HttpErrorLike {
  response?: { status?: number; data?: { detail?: unknown } }
  statusCode?: number
  details?: { detail?: unknown }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function mockOutcome(token: string): void {
  if (token === EMAIL_LINKS_MOCK_EXPIRED_TOKEN) throw new EmailLinkError('expired')
  if (token === EMAIL_LINKS_MOCK_USED_TOKEN) throw new EmailLinkError('already')
}

/**
 * fair-api answers a bad confirmation token with 401 "Expired token" /
 * "Invalid token", an address confirmed earlier with "Email already verified"
 * or, from Cognito, "…Current status is CONFIRMED".
 */
export function toEmailLinkError(error: unknown): EmailLinkError {
  if (error instanceof EmailLinkError) return error
  const http = error as HttpErrorLike | null
  const status = http?.response?.status ?? http?.statusCode
  const detail = String(http?.response?.data?.detail ?? http?.details?.detail ?? '').toLowerCase()
  if (detail.includes('already verified') || detail.includes('status is confirmed')) {
    return new EmailLinkError('already')
  }
  if (status !== undefined && status >= 400 && status < 500) {
    return new EmailLinkError('expired')
  }
  return new EmailLinkError('unknown')
}

/** Email 04: confirms the new address, returns it for the success screen. */
export async function confirmEmailChange(token: string): Promise<{ email: string }> {
  if (!hasEmailLinksApi('confirm_email_change')) {
    await wait(MOCK_LATENCY_MS)
    mockOutcome(token)
    return { email: MOCK_NEW_EMAIL }
  }
  try {
    const { data } = await apiClient.post<{ email: string }>('/auth/change-email/confirm', { token })
    return data
  } catch (error) {
    throw toEmailLinkError(error)
  }
}

/** Emails 03 / 05: new password, previous email restored, every session signed out. */
export async function secureAccount(token: string, password: string): Promise<void> {
  if (!hasEmailLinksApi('secure_account')) {
    await wait(MOCK_LATENCY_MS)
    mockOutcome(token)
    return
  }
  try {
    await apiClient.post('/auth/secure-account', { token, password })
  } catch (error) {
    throw toEmailLinkError(error)
  }
}

/** Marketing group switched off by the footer link: newsletter, trends, guides, for_you… */
export type NotificationGroup = 'newsletter' | 'trends' | 'guides' | 'for_you' | 'premium' | 'rewards' | 'marketing'

export async function unsubscribeGroup(token: string): Promise<{ group: NotificationGroup }> {
  if (!hasEmailLinksApi('group_unsubscribe')) {
    await wait(MOCK_LATENCY_MS)
    mockOutcome(token)
    return { group: 'newsletter' }
  }
  try {
    const { data } = await apiClient.delete<{ group: NotificationGroup }>('/notifications/groups/unsubscribe', {
      params: { token },
    })
    return data
  } catch (error) {
    throw toEmailLinkError(error)
  }
}

export async function resubscribeGroup(token: string): Promise<void> {
  if (!hasEmailLinksApi('group_unsubscribe')) {
    await wait(MOCK_LATENCY_MS)
    return
  }
  await apiClient.post('/notifications/groups/unsubscribe/undo', null, { params: { token } })
}

export interface TurnedOffAlert {
  kind: string
  /** Absolute or site path of the product; fair-api does not return it yet (BE-210). */
  productUrl: string | null
}

/** "Turn off this alert" (BE-117): signed token, no expiry, no sign-in. Real API. */
export async function turnOffAlert(token: string): Promise<TurnedOffAlert> {
  try {
    const { data } = await apiClient.delete<{ kind: string; product_url?: string | null }>(
      '/notifications/subscriptions/unsubscribe',
      { params: { token } },
    )
    return { kind: data.kind, productUrl: data.product_url ?? null }
  } catch (error) {
    throw toEmailLinkError(error)
  }
}

export async function turnOnAlert(token: string): Promise<void> {
  if (!hasEmailLinksApi('alert_undo')) {
    await wait(MOCK_LATENCY_MS)
    return
  }
  await apiClient.post('/notifications/subscriptions/resubscribe', null, { params: { token } })
}
